// components\missions\claims-history.jsx

import React, { Fragment, useEffect } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import Image from "next/image";
import { fetchUserReferralsByUID } from "@/utils/firebaseFunctions";
import { useState } from "react";
import clipboardCopy from "clipboard-copy";
import { toast } from "react-toastify";
import { toTitleCase } from '@/utils/helpers/helperFunctions';

export default function ClaimsHistory({ user, address, userRewards, userPoints, formatRelativeTime }) {
  const [tabIndex, setTabIndex] = React.useState(0);
  const [userLoading, setUserLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);

  const [userReferrals, setUserReferrals] = useState([]);
  const [userReferralsLoading, setUserReferralsLoading] = useState(true);

  const handleCopyToClipboard = (text) => {
    clipboardCopy(text);
    toast.success("Copied to clipboard!");
  };

  const fetchUserReferrals = async () => {
    try {
      const response = await fetchUserReferralsByUID(user?.user_id);
      if (response && response.referred_users && response.referred_users.length > 0) {
        const sortedUsers = response.referred_users.sort((a, b) => {
          if (a.timestamp_dt && b.timestamp_dt) {
            return b.timestamp_dt - a.timestamp_dt;
          }
          return 0;
        });

        setUserReferrals(sortedUsers);
        setUserReferralsLoading(false);
      } else {
        setUserReferrals(response);
        setUserReferralsLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch or sort user referrals:", error);
      setUserReferralsLoading(false);
    }
  };

  useEffect(() => {
    if (user && address && userLoading === true) {
      setUserLoading(false);
      fetchUserReferrals();
    }

    if (!address && userLoading) {
      setUserLoading(false);
      setUserReferralsLoading(false);
    }
  }, [user, userLoading, address]);

  useEffect(() => {
    if(address){
      setUserLoading(true);
    }
  }, [address]);

  return (
    <div className="flex flex-col">
      {user ? (
        <Fragment>
          <Tabs className={"claimsHistoryTabs"} selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
            <TabList className={"relative"}>
              <Tab>
                <div className="tab-title text-lg lg:text-xl flex flex-row justify-stretch items-center">
                  <span>Your Claims</span>
                  {userPoints && (
                    <span className="text-sm ml-3 flex flex-row justify-stretch items-center pl-2 pr-3 py-1 bg-metaxseed-blue rounded-xl text-white">
                      <Image src="https://res.cloudinary.com/dot2onqdb/image/upload/v1729068489/gold_lr_h4yg2m.png" className="w-5 h-5 mr-1" alt="MXS GOLD" width={200} height={200} />
                      {userPoints.all_time_mxs_gold}
                    </span>
                  )}
                </div>
              </Tab>
              <Tab>
                <div className="tab-title text-lg lg:text-xl flex flex-row justify-stretch items-center">
                  <span>Bonus Awarded</span>
                  {userPoints && (
                    <span className="text-sm ml-3 flex flex-row justify-stretch items-center pl-2 pr-3 py-1 bg-metaxseed-blue rounded-xl text-white">
                      <Image src="https://res.cloudinary.com/dot2onqdb/image/upload/v1729068489/gold_lr_h4yg2m.png" className="w-5 h-5 mr-1" alt="MXS GOLD" width={200} height={200} />
                      N/A
                    </span>
                  )}
                </div>
              </Tab>
            </TabList>

            <TabPanel className={"px-0 pt-12 pb-24"}>
              <Fragment>
                {Array.isArray(userRewards) && userRewards.length > 0 ? (
                  <div className="hidden md:flex flex-row items-stretch justify-start w-full px-4 xl:px-8 mb-2 gothamBook">
                    <div className="text-white text-sm xl:text-base gothamMedium flex flex-1 items-center">Platform</div>
                    <div className="text-white text-sm xl:text-base gothamMedium flex flex-1 items-center">Type</div>
                    <div className="text-white text-sm xl:text-base gothamMedium flex flex-1 items-center">Sub Event</div>
                    <div className="text-white text-sm xl:text-base gothamMedium flex flex-1 items-center max-w-36">Rewards</div>
                    <div className="text-white text-sm xl:text-base gothamMedium flex flex-1 items-center max-w-32">Date</div>
                  </div>
                ) : null}
                {Array.isArray(userRewards) && userRewards.length > 0 ? (
                  userRewards
                    .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)
                    .map((reward, i) => {
                      // * Removing Game Web prefix in the Claim History to those WebPlay Titles
                      let modifiedGameName = toTitleCase(reward.game_name.replace(/_/g, " ")).split("Game Web")[1];

                      if (modifiedGameName === undefined) {
                        modifiedGameName = toTitleCase(reward.game_name.replace(/_/g, " "));
                      }

                      // console.log(modifiedGameName);
                      return (
                        <Fragment key={i}>
                          {/* Desktop Table Row */}
                          <div className="hidden md:flex items-center justify-between bg-metaxseed-content-gray rounded-xl px-4 xl:px-8 py-4 mb-4 gothamBook">
                            <div className="flex items-center flex-1 text-sm xl:text-base min-w-0">
                              <span className="truncate" title={modifiedGameName}>{modifiedGameName}</span>
                            </div>
                            <div className="flex items-center flex-1 text-sm xl:text-base min-w-0">
                              <span className="truncate" title={toTitleCase(reward.type.replace(/_/g, " "))}>{toTitleCase(reward.type.replace(/_/g, " "))}</span>
                            </div>
                            <div className="flex items-center flex-1 text-sm xl:text-base min-w-0">
                              <span className="truncate" title={reward.sub_event ? toTitleCase(reward.sub_event.replace(/_/g, " ")) : "N/A"}>{reward.sub_event ? toTitleCase(reward.sub_event.replace(/_/g, " ")) : "N/A"}</span>
                            </div>
                            <span className="flex items-center flex-1 max-w-36 text-sm xl:text-base">{reward.mxs_gold_claimed} MXS Gold</span>
                            <span className="flex items-center flex-1 max-w-32 text-sm xl:text-base">{formatRelativeTime(reward.firebase_timestamp.seconds)}</span>
                          </div>

                          {/* Mobile Card */}
                          <div className="md:hidden bg-metaxseed-content-gray rounded-xl px-4 py-4 mb-4 gothamBook">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-metaxseed-text-gray text-xs">Platform</span>
                                <span className="text-white text-sm font-medium truncate ml-2" title={modifiedGameName}>{modifiedGameName}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-metaxseed-text-gray text-xs">Type</span>
                                <span className="text-white text-sm font-medium truncate ml-2" title={toTitleCase(reward.type.replace(/_/g, " "))}>{toTitleCase(reward.type.replace(/_/g, " "))}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-metaxseed-text-gray text-xs">Sub Event</span>
                                <span className="text-white text-sm font-medium truncate ml-2" title={reward.sub_event ? toTitleCase(reward.sub_event.replace(/_/g, " ")) : "N/A"}>{reward.sub_event ? toTitleCase(reward.sub_event.replace(/_/g, " ")) : "N/A"}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-metaxseed-text-gray text-xs">Rewards</span>
                                <span className="text-white text-sm font-medium">{reward.mxs_gold_claimed} MXS Gold</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-metaxseed-text-gray text-xs">Date</span>
                                <span className="text-white text-sm font-medium">{formatRelativeTime(reward.firebase_timestamp.seconds)}</span>
                              </div>
                            </div>
                          </div>
                        </Fragment>
                      )
                    })
                ) : (
                  <div className="flex items-center justify-center bg-metaxseed-content-gray rounded-xl px-4 py-4 mb-4 mt-8">
                    <span className="text-metaxseed-light-gray">No rewards claimed yet</span>
                  </div>
                )}

                {Array.isArray(userRewards) && userRewards.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-2 mt-8">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-metaxseed-blue rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-metaxseed-dark-blue transition-colors duration-200"
                    >
                      Previous
                    </button>
                    <span className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-white">
                      Page {currentPage + 1} of {Math.ceil(userRewards.length / itemsPerPage)}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => p + 1)}
                      disabled={(currentPage + 1) * itemsPerPage >= userRewards.length}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-metaxseed-blue rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-metaxseed-dark-blue transition-colors duration-200"
                    >
                      Next
                    </button>
                  </div>
                )}
              </Fragment>
            </TabPanel>

            <TabPanel className={"px-0 pt-12 pb-24"}>
              <Fragment>
                {/*
                {!userReferralsLoading && userReferrals && userReferrals.referred_users && userReferrals.referred_users.length > 0 ? (
                  <Fragment>
                    <div className="flex flex-row items-stretch justify-start w-full px-4 xl:px-8 mb-2 gothamBook">
                      <div className="text-white text-sm xl:text-base gothamMedium flex flex-1 items-center">Referred UID</div>
                      <div className="text-white text-sm xl:text-base gothamMedium flex flex-1 items-center">User's MXS Gold</div>
                      <div className="text-white text-sm xl:text-base gothamMedium flex flex-1 items-center">Your Bonus</div>
                    </div>

                    {userReferrals?.referred_users
                      ?.filter((refUser) => refUser.is_active)
                      .map((user, i) => (
                        <div key={i} className="flex items-center justify-between bg-metaxseed-content-gray rounded-xl px-4 xl:px-8 py-3 mb-4 gothamBook">
                          <div className="flex items-center flex-1 text-sm xl:text-base">
                            <span className="flex items-center text-sm xl:text-base mr-2">{user.uid.substring(0, 3) + "..." + user.uid.substring(user.uid.length - 3)} </span>

                            <button onClick={() => handleCopyToClipboard(user.uid)} className="cursor-pointer text-zinc-400 dark:text-zinc-600 hover:text-zinc-600 dark:hover:text-zinc-400">
                              <Icon icon="solar:copy-linear" className="text-metaxseed-gray" />
                            </button>
                          </div>
                          <div className="flex items-center flex-1 text-sm xl:text-base">{user.all_time_mxs_gold_with_bonuses}</div>
                          <span className="flex items-center flex-1 text-sm xl:text-base">
                            {Math.floor(user.all_time_mxs_gold_with_bonuses / 10)}
                            <Image src="https://res.cloudinary.com/dot2onqdb/image/upload/v1729068489/gold_lr_h4yg2m.png" className="w-6 h-6 ml-2" alt="MXS GOLD" width={200} height={200} />
                          </span>
                        </div>
                      ))}
                  </Fragment>
                    ) : (
                    <div className="flex items-center justify-center bg-metaxseed-content-gray rounded-xl px-4 py-4 mb-4 mt-8">
                    <span className="text-metaxseed-light-gray">No bonus claims yet</span>
                    </div>
                )}
                */}
                
                <div className="flex items-center justify-center bg-metaxseed-content-gray rounded-xl px-4 py-4 mb-4 mt-8">
                    <span className="text-metaxseed-light-gray">This section is under maintenance right now.</span>
                  </div>
              </Fragment>
            </TabPanel>
          </Tabs>
        </Fragment>
      ) : (
        <div className="flex items-center justify-center bg-metaxseed-content-gray rounded-xl px-4 py-4 mb-4 mt-8">
          <span className="text-metaxseed-light-gray">Connect your wallet to view rewards</span>
        </div>
      )}
    </div>
  );
}
